import axios from 'axios';
import { clearHandlingFeeLimitCookieFunction } from 'components/handling-fee-limit-banner-fi/handlingFeeCookie';
import type { VueCookies } from 'vue-cookies';

import { sendEventFunction } from '@/composables/eventHub';
import { usernameCookie } from '@/composables/login';
import type { FischerPlugin } from '@/lib/fischerPlugin';
import { markAsNotIDS } from '@/utils/idsTools';

import { useCookies } from './cookies';
import { useFischer } from './fischerPlugin';

export interface LogoutReason {
  reason: string;
}

export const logoutFromSSO = (redirectRelative: string): Promise<void> => {
  const redirect = new URL(redirectRelative, window.location.origin);
  window.location.href = `${
    import.meta.env.VITE_OPENID_SERVICES
  }authorizationserver/logout.do?redirect_uri=${encodeURIComponent(String(redirect))}`;

  return new Promise(() => {});
};

export const logoutFunction = async (
  { reason, successUrl }: { reason?: string; successUrl?: string } = {},
  fischer: FischerPlugin,
  cookies: VueCookies,
  setErrorOccured?: () => void,
) => {
  if (IS_SSR) {
    return;
  }
  const logoutSuccessPage = successUrl || fischer.pages.logoutSuccess().url;
  const redirect = reason ? `${logoutSuccessPage}?reason=${reason}` : logoutSuccessPage;
  try {
    cookies.remove(usernameCookie);
    fischer.setAssistedService();
    clearHandlingFeeLimitCookieFunction(cookies);

    if (IS_SHOWROOM) {
      cookies.remove('AccessToken');
      cookies.remove('IdToken');
      cookies.remove('CartId');
    } else {
      markAsNotIDS();
      await axios.post('/webapi/shop/userapi/Logout', {});
    }
    await sendEventFunction(
      fischer,
      cookies,
      'custom',
      {},
      'logout',
      reason ? { reason } : undefined,
    );
    await logoutFromSSO(redirect);
  } catch (error) {
    console.error(error);
    setErrorOccured?.();
  }
};

export const logoutIfTokenInvalidFunction = async (
  error: undefined | { message?: string },
  fischer: FischerPlugin,
  logout: (reason: LogoutReason) => Promise<void>,
) => {
  if (
    error?.message === 'GraphQL error: Invalid token' ||
    error?.message === 'ApolloError: Invalid token' ||
    error?.message === 'Invalid token' ||
    fischer.user.isIdTokenValid()
  ) {
    await logout({ reason: 'InvalidToken' });
  }
};

export const useUser = () => {
  const fischer = useFischer();
  const { cookies } = useCookies();

  const logout = (params: Parameters<typeof logoutFunction>[0]) =>
    logoutFunction(params, fischer, cookies);

  const logoutIfTokenInvalid = (error: Parameters<typeof logoutIfTokenInvalidFunction>[0]) =>
    logoutIfTokenInvalidFunction(error, fischer, logout);

  return {
    logoutIfTokenInvalid,
    logout,
    logoutFromSSO,
  };
};
